import React, { useEffect, useState, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";

import axios from "../../../../utils/axiosInstance";

import { Notify } from "../../../../components";

import DocIcon from "../../../../assets/icons/DocIcon.svg";
import PdfIcon from "../../../../assets/icons/PdfIcon.svg";
import CsvIcon from "../../../../assets/icons/csvIcon.svg";
import TextIcon from "../../../../assets/icons/textIcon.svg";
import ImgIcon from "../../../../assets/icons/imgIcon.svg";
import AddAttachmentIcon from "../../../../assets/icons/attachments.svg";

import { getUserDetailsAction } from "../../../../actions/global-action";

import {
  setFileCanceller,
  setFileUploadProgress,
} from "../../actions/slidebox-action";

import {
  Overlay,
  ModalHeader,
  PrimaryButton,
  SecondaryButton,
} from "../../../../components";

const SUPPORTED_EXTENSIONS = [
  "docx",
  "doc",
  "txt",
  "csv",
  "xls",
  "xlsx",
  "bmp",
  "pdf",
  "jpg",
  "jpeg",
  "png",
];

const AddAttachment = ({
  loggedUserDetails,
  fileList = [],
  setFileUploadProgress,
  setFileCanceller,
  getLoggedUserDetails,
  selectedListType,
  selectedList,
  attachInput,
  setAttachInput,
}) => {
  const navigate = useNavigate();

  const [attachment, setAttachment] = useState(null);
  const [attachModal, setAttachModal] = useState(false);
  const [fileSavingPath, setFileSavingPath] = useState(null);

  const fileInput = useRef(null);

  const filenameParts = useMemo(() => {
    return attachment?.name?.split(".") || [];
  }, [attachment]);

  const fileExtension = useMemo(() => {
    return (
      filenameParts[filenameParts.length - 1]?.toLowerCase() || ""
    );
  }, [filenameParts]);

  useEffect(() => {
    if (attachInput && fileInput.current) {
      fileInput.current.click();
      setAttachInput(false);
    }
  }, [attachInput, setAttachInput]);

  const sizeInMb = (size) => {
    const units = ["bytes", "KB", "MB", "GB"];

    let unitIndex = 0;
    let value = parseInt(size, 10) || 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }

    return `${value.toFixed(
      value < 10 && unitIndex > 0 ? 1 : 0
    )} ${units[unitIndex]}`;
  };

  const getFileIcon = () => {
    if (fileExtension === "pdf") {
      return PdfIcon;
    }

    if (["csv", "xls", "xlsx"].includes(fileExtension)) {
      return CsvIcon;
    }

    if (["docx", "doc"].includes(fileExtension)) {
      return DocIcon;
    }

    if (
      ["jpg", "jpeg", "png", "bmp"].includes(fileExtension)
    ) {
      return ImgIcon;
    }

    return TextIcon;
  };

  const toggleUploadFile = async (file) => {
    if (!file) {
      Notify({ value: "No files selected" });
      return;
    }

    try {
      const dxAccountId =
        loggedUserDetails?.dxAccountId;

      if (!dxAccountId) {
        Notify({
          value: "Account information not found",
        });
        return;
      }

      const rootPath = `slidebox/${dxAccountId}/`;
      const rootPathDocument = `documents/${dxAccountId}/`;

      file.uploadedAt = Date.now();

      const fileNameArray = file.name.split(".");
      const extension =
        fileNameArray[fileNameArray.length - 1]?.toLowerCase();

      const isDocumentFile = [
        "docx",
        "doc",
        "csv",
        "xls",
        "xlsx",
        "pdf",
      ].includes(extension);

      const filePath = isDocumentFile
        ? rootPathDocument
        : rootPath;

      setFileSavingPath(filePath);

      const uploadingIndex = fileList.length;

      const response = await axios.get(
        "api/slidebox/getPresignedPutUrl",
        {
          params: {
            fileName: `${filePath}${file.name}`,
            fileType: file.type,
          },
        }
      );

      await uploadToS3Direct(
        response.data,
        file,
        uploadingIndex,
        1
      );

      getLoggedUserDetails();
    } catch (error) {
      console.warn(error);

      Notify({
        value: "Failed to upload file",
      });
    }
  };

  const uploadToS3Direct = async (
    data,
    file,
    fileIndex,
    filesLength
  ) => {
    try {
      const fileData = new FormData();

      Object.keys(data.params).forEach((key) => {
        if (key === "content-type") {
          fileData.append(key, "");
        } else {
          fileData.append(key, data.params[key]);
        }
      });

      fileData.append("file", file);

      const config = {
        maxContentLength: Infinity,
        maxBodyLength: Infinity,

        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) /
              progressEvent.total
          );

          setFileUploadProgress({
            fileIndex,
            percentCompleted,
          });
        },

        cancelToken: new axios.CancelToken(
          (cancel) => {
            setFileCanceller({
              fileIndex,
              canceller: cancel,
            });
          }
        ),
      };

      const response = await axios.post(
        data.endpoint_url,
        fileData,
        config
      );

      await handleOnUploadToDataBase(
        response,
        file,
        filesLength
      );

      getLoggedUserDetails();
    } catch (error) {
      console.warn(error);

      if (!axios.isCancel(error)) {
        Notify({
          value: "Upload failed",
        });
      }
    }
  };

  const handleOnUploadToDataBase = async (
    response,
    file,
    filesLength
  ) => {
    try {
      if (
        !SUPPORTED_EXTENSIONS.includes(fileExtension)
      ) {
        Notify({
          value: "Unsupported file format",
        });

        return;
      }

      const fileIndex = 1;

      const data = {
        originalFileName: file.name,
        fileName: file.name,
        fileSize: file.size,
        uniquePath: "slidebox",
        isLargeFile: file.size > 20971520,
        fileIndex,
        stain: "",
        filesLength,
        selectedFolderId: selectedList,
        selectedFolderType: selectedListType,
      };

      const fileUploadUrl =
        "api/slidebox/addSlideboxDocuments";

      const uploadResponse = await axios.post(
        fileUploadUrl,
        data
      );

      if (uploadResponse.data.msg === "success") {
        if (filesLength === fileIndex) {
          navigate("/", {
            state: {
              folderSelected: true,
              folderPath: fileSavingPath,
            },
          });
        }

        getLoggedUserDetails();

        Notify({
          value: "File Uploaded Successfully",
        });
      }
    } catch (error) {
      console.warn(error);

      Notify({
        value: "Please select file",
      });
    }
  };

  return (
    <>
      <input
        type="file"
        name="file"
        ref={fileInput}
        onChange={(e) => {
          const selectedFile =
            e.target.files?.[0];

          if (selectedFile) {
            setAttachment(selectedFile);
            setAttachModal(true);
          }

          e.target.value = "";
        }}
        style={{ display: "none" }}
      />

      {attachModal && attachment && (
        <>
          <Overlay />

          <div className="prompt">
            <motion.div
  initial={{ opacity: 0 }}
  animate={{ opacity: 1 }}
  transition={{ duration: 0.3 }}
>
              <ModalHeader
                img={AddAttachmentIcon}
                alt="attachment"
                title="Add file to case"
                onClick={() =>
                  setAttachModal(false)
                }
                id="modalHeader"
              />

              <div className="prompt-middle attachment-modal">
                <img
                  src={getFileIcon()}
                  className="file-type-icon"
                  alt={fileExtension}
                />

                <div className="file-details">
                  <h4 className="file-name">
                    {attachment.name}
                  </h4>

                  <p className="file-size">
                    {sizeInMb(attachment.size)}
                  </p>
                </div>
              </div>

              <div className="prompt-bottom">
                <SecondaryButton
                  label="Cancel"
                  onClick={() =>
                    setAttachModal(false)
                  }
                />

                <PrimaryButton
                  label="Attach"
                  onClick={() => {
                    toggleUploadFile(attachment);
                    setAttachModal(false);
                  }}
                />
              </div>
            </motion.div>
          </div>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  loggedUserDetails:
    state.Global.loggedUserDetails,

  selectedListData:
    state.SlideBox.selectedListData,

  ...state.SlideBox.selectedFolderData,

  ...state.SlideBox.selectedListData,

  fileList: state.SlideBox.fileList,
});

const mapDispatchToProps = (dispatch) => ({
  getLoggedUserDetails: () =>
    dispatch(getUserDetailsAction()),

  setFileUploadProgress: (payload) =>
    dispatch(setFileUploadProgress(payload)),

  setFileCanceller: (payload) =>
    dispatch(setFileCanceller(payload)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AddAttachment);